/**
 * Build triggers on the app page (plan P33, SPEC decision 31): what each trigger says in words, the webhook's state, the
 * last thing a push did, and the add/edit form (values, field errors, the request body). Pure: the card renders it. Client
 * copy names the client's own tools (the repository, Bitbucket, branches) and never a build service (decision 23).
 */
import { branchPatternProblem, defaultTriggerPaths, globMatches, pathPatternProblem, TRIGGER_PATHS_MAX, type BuildTrigger, type BuildTriggerCreateRequest, type Environment, type TriggerWebhook } from '@hangar/shared';
import type { Profile } from '@/api/schemasProfile';
import { timeAgo } from '@/lib/format';

export type Tone = 'success' | 'warning' | 'danger' | 'neutral';

export interface WebhookState { label: string; tone: Tone; help: string | null; retry: boolean }

/** The repository webhook as a badge and one line of help. */
export function webhookState(w: TriggerWebhook): WebhookState {
  switch (w.status) {
    case 'registered': return { label: 'Listening for pushes', tone: 'success', help: w.repository ? `Bitbucket sends every push of ${w.repository} to Hangar` : null, retry: false };
    case 'needs_permission': return { label: 'Needs the Webhooks permission', tone: 'warning', help: w.message ?? 'Give the access token "Webhooks: Read and write", then select Retry', retry: true };
    case 'unreachable': return { label: 'Hangar is not reachable yet', tone: 'warning', help: w.message, retry: true };
    case 'failed': return { label: 'Webhook not registered', tone: 'danger', help: w.message, retry: true };
    case 'no_repository': return { label: 'No repository linked', tone: 'neutral', help: 'Link the app\'s repository above: triggers start builds when it receives a push', retry: false };
    default: return { label: 'Not active', tone: 'neutral', help: w.message ?? 'Turn a trigger on and Hangar registers the webhook on the repository', retry: false };
  }
}

/** "Any change" or the path patterns. */
export const pathsText = (paths: readonly string[]): string => (paths.length === 0 ? 'Any change' : paths.join(', '));

/** "Internal APK · Staging" (or the id when the profile or environment is gone). */
export function targetText(t: Pick<BuildTrigger, 'profileId' | 'environmentId'>, profiles: readonly Profile[], environments: readonly Environment[]): string {
  const profile = profiles.find((p) => p.id === t.profileId)?.name ?? 'Archived profile';
  const env = environments.find((e) => e.id === t.environmentId)?.name ?? 'Archived environment';
  return `${profile} · ${env}`;
}

const DELIVERY_TEXT: Record<NonNullable<BuildTrigger['lastDelivery']>['outcome'], string> = {
  queued: 'queued',
  skipped_paths: 'skipped: no changes under the paths',
  skipped_environment: 'skipped: the environment does not build this branch',
  superseded: 'replaced by a newer push',
};

export interface Activity { text: string; tone: Tone; buildId: string | null; buildNumber: number | null }

/** The newest of the last push and the last run, in words; null when nothing happened yet. */
export function lastActivity(t: Pick<BuildTrigger, 'lastDelivery' | 'lastRun'>, now: number = Date.now()): Activity | null {
  const run = t.lastRun;
  const delivery = t.lastDelivery;
  if (delivery && (!run || delivery.at > run.at) && delivery.outcome !== 'queued') {
    return { text: `Push to ${delivery.branch} (${delivery.commitSha.slice(0, 7)}) ${DELIVERY_TEXT[delivery.outcome]} · ${timeAgo(delivery.at, now)}`, tone: 'neutral', buildId: null, buildNumber: null };
  }
  if (delivery && (!run || delivery.at > run.at)) {
    return { text: `Push to ${delivery.branch} (${delivery.commitSha.slice(0, 7)}) waiting 30 s for newer pushes · ${timeAgo(delivery.at, now)}`, tone: 'neutral', buildId: null, buildNumber: null };
  }
  if (!run) return null;
  const first = run.builds[0];
  const when = timeAgo(run.at, now);
  const sha = run.commitSha.slice(0, 7);
  if (run.status === 'started' && first) return { text: `Started build #${first.number} from ${sha} on ${run.branch} · ${when}`, tone: 'success', buildId: first.id, buildNumber: first.number };
  if (run.status === 'skipped') return { text: `Skipped ${sha}: ${run.message ?? 'nothing to build'} · ${when}`, tone: 'neutral', buildId: first?.id ?? null, buildNumber: first?.number ?? null };
  if (run.status === 'failed') return { text: `Build not started for ${sha}: ${run.message ?? 'Hangar refused it'} · ${when}`, tone: 'danger', buildId: null, buildNumber: null };
  return { text: `Push to ${run.branch} (${sha}) is starting · ${when}`, tone: 'neutral', buildId: null, buildNumber: null };
}

export interface TriggerFormValues { branchPattern: string; paths: string; profileId: string; environmentId: string; enabled: boolean }
export type TriggerFormErrors = Partial<Record<keyof TriggerFormValues, string>>;

/** The environments a profile builds for (live ones), in the project's order. */
export function environmentsOf(profile: Profile | undefined, environments: readonly Environment[]): Environment[] {
  if (!profile) return [];
  const bound = new Set(profile.environments.map((b) => b.environmentId));
  return environments.filter((e) => !e.archivedAt && bound.has(e.id));
}

/** A new trigger: the default branch, the app's folder, the first profile and its first environment. */
export function newTriggerValues(app: { workingDirectory: string; defaultBranch: string | null }, profiles: readonly Profile[], environments: readonly Environment[]): TriggerFormValues {
  const profile = profiles[0];
  const env = environmentsOf(profile, environments).find((e) => !e.isProtected) ?? environmentsOf(profile, environments)[0];
  return { branchPattern: app.defaultBranch ?? 'main', paths: defaultTriggerPaths(app.workingDirectory).join('\n'), profileId: profile?.id ?? '', environmentId: env?.id ?? '', enabled: true };
}

export const valuesOf = (t: BuildTrigger): TriggerFormValues => ({ branchPattern: t.branchPattern, paths: t.paths.join('\n'), profileId: t.profileId, environmentId: t.environmentId, enabled: t.enabled });

/** One pattern per line (commas also split); blanks dropped. */
export const parsePaths = (text: string): string[] => [...new Set(text.split(/[\n,]/).map((p) => p.trim()).filter(Boolean))];

/** A protected environment takes a branch its rules allow (literal) or one of its patterns verbatim (the API's rule). */
function protectedProblem(pattern: string, env: Environment): string | undefined {
  if (!env.isProtected) return undefined;
  const ok = env.allowedBranchPatterns.includes(pattern) || (!pattern.includes('*') && env.allowedBranchPatterns.some((p) => globMatches(pattern, p)));
  return ok ? undefined : `${env.name} is protected and builds only ${env.allowedBranchPatterns.join(', ') || 'no branch'}`;
}

/** Field errors, checked before the request (the API checks again). */
export function triggerFormErrors(v: TriggerFormValues, environments: readonly Environment[]): TriggerFormErrors {
  const errors: TriggerFormErrors = {};
  const branch = branchPatternProblem(v.branchPattern);
  if (branch) errors.branchPattern = `Branch pattern ${branch}`;
  const paths = parsePaths(v.paths);
  const badPath = paths.map((p) => [p, pathPatternProblem(p)] as const).find(([, problem]) => problem);
  if (badPath) errors.paths = `${badPath[0]}: ${badPath[1]}`;
  else if (paths.length > TRIGGER_PATHS_MAX) errors.paths = `At most ${TRIGGER_PATHS_MAX} path patterns`;
  if (!v.profileId) errors.profileId = 'Pick a build profile';
  const env = environments.find((e) => e.id === v.environmentId);
  if (!env) errors.environmentId = 'Pick an environment the profile builds for';
  else if (!errors.branchPattern) {
    const p = protectedProblem(v.branchPattern, env);
    if (p) errors.branchPattern = p;
  }
  return errors;
}

export const bodyOf = (v: TriggerFormValues): BuildTriggerCreateRequest => ({ branchPattern: v.branchPattern.trim(), paths: parsePaths(v.paths), profileId: v.profileId, environmentId: v.environmentId, enabled: v.enabled });
